import React from 'react';
import { motion } from 'framer-motion';
import { getTotalLevels } from '../data/levels';

interface LevelSelectProps {
  currentLevel: number;
  onLevelSelect: (levelId: number) => void;
  onClose: () => void;
}

const LevelSelect: React.FC<LevelSelectProps> = ({ currentLevel, onLevelSelect, onClose }) => {
  const totalLevels = getTotalLevels();
  
  const levels = Array.from({ length: totalLevels }, (_, i) => i + 1);
  
  const getLevelName = (levelId: number) => {
    const levelNames = [
      '', // 0 index
      'Getting Started', 'Animal Friends', 'Nature\'s Bounty', 'Home Sweet Home', 'Ocean Waves',
      'Food for Thought', 'Colorful World', 'Time Flies', 'Musical Notes', 'Space Adventure',
      'Quick Challenge', 'Garden Party', 'Tech World', 'Mountain High', 'Speed Round',
      'Forest Friends', 'City Lights', 'Mini Puzzle', 'Ocean Deep', 'Final Challenge'
    ];
    return levelNames[levelId] || `Level ${levelId}`;
  };
  
  const getLevelSize = (levelId: number) => {
    // Determine grid size based on level
    if (levelId === 11 || levelId === 18) return '3×3';
    if (levelId >= 12 && levelId <= 16) return '5×5';
    if (levelId === 19 || levelId === 20) return '5×5';
    return '4×4';
  };
  
  const getLevelDifficulty = (levelId: number) => {
    if (levelId <= 5) return 'Easy';
    if (levelId <= 10) return 'Medium';
    if (levelId <= 15) return 'Hard';
    return 'Expert';
  };
  
  const getDifficultyColor = (levelId: number) => {
    if (levelId <= 5) return '#4CAF50'; // Green
    if (levelId <= 10) return '#FF9800'; // Orange
    if (levelId <= 15) return '#F44336'; // Red
    return '#9C27B0'; // Purple
  };

  return (
    <motion.div 
      className="level-select-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        className="level-select-modal"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
      >
        <div className="level-select-header">
          <h2>Choose Your Level</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="level-grid">
          {levels.map((levelId) => (
            <motion.button
              key={levelId}
              className={`level-button ${levelId === currentLevel ? 'current' : ''} ${levelId <= currentLevel ? 'unlocked' : 'locked'}`}
              onClick={() => levelId <= currentLevel && onLevelSelect(levelId)}
              whileHover={{ scale: levelId <= currentLevel ? 1.05 : 1 }}
              whileTap={{ scale: levelId <= currentLevel ? 0.95 : 1 }}
              disabled={levelId > currentLevel}
            >
              <div className="level-number">{levelId}</div>
              <div className="level-name">{getLevelName(levelId)}</div>
              <div className="level-info">
                <span className="level-size">{getLevelSize(levelId)}</span>
                <span 
                  className="level-difficulty"
                  style={{ color: getDifficultyColor(levelId) }}
                >
                  {getLevelDifficulty(levelId)}
                </span>
              </div>
              {levelId > currentLevel && (
                <div className="locked-overlay">🔒</div>
              )}
            </motion.button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LevelSelect;

